import React, { useMemo, useState } from "react";
import { useTranslation } from 'react-i18next';
import azureIcon from "../Assets/azure.svg";
import awsIcon from "../Assets/aws-svgrepo-com.svg";
import gcpIcon from "../Assets/gcp-svgrepo-com.svg";

// ✅ Hook backend
import { useAzureResources } from "../../hooks/useAzureResources";

// ✅ Azure TOP 100
import { azureTop100Whitelist } from "../../Config/azure/top100Whitelist";
import { azureTop100Meta } from "../../Config/azure/top100Meta";
import { azureIconsByTypeTop100 } from "../../Config/azure/azure-iconsByType.top100";

// ✅ AWS TOP 100
import { awsTop100Whitelist } from "../../Config/aws/top100Whitelist";
import { awsTop100Meta } from "../../Config/aws/top100Meta";
import { awsIconsByTypeTop100 } from "../../Config/aws/aws-iconsByType.top100";

// ✅ GCP TOP 100
import { gcpTop100Whitelist } from "../../Config/gcp/top100Whitelist";
import { gcpTop100Meta } from "../../Config/gcp/top100Meta";
import { gcpIconsByTypeTop100 } from "../../Config/gcp/gcp-iconsByType.top100";

// ✅ Microsoft Fabric
import { fabricTop100Whitelist } from "../../Config/fabric/top100Whitelist";
import { fabricTop100Meta } from "../../Config/fabric/top100Meta";
import { fabricIconsByTypeTop100 } from "../../Config/fabric/fabric-iconsByType.top100";


type ComponentItem = {
  type: string;
  label: string;
  icon: string;
  category: string;
};

// ✅ Fonction générique pour construire les ressources par catégorie depuis top100
function buildTop100Resources(
  whitelist: readonly string[],
  meta: Record<string, { displayName: string; category: string }>,
  icons: Record<string, string>
): Record<string, ComponentItem[]> {
  const byCategory: Record<string, ComponentItem[]> = {};

  for (const key of whitelist) {
    const resourceMeta = meta[key];
    const icon = icons[key];

    // Ignore les ressources sans meta, catégorie ou icône
    if (!resourceMeta || !resourceMeta.category || !icon) {
      console.warn("⛔ Ressource ignorée (incomplète) :", key);
      continue;
    }

    const label = resourceMeta.displayName || key;
    const category = resourceMeta.category;

    const item: ComponentItem = { type: key, label, icon, category };
    if (!byCategory[category]) byCategory[category] = [];
    byCategory[category].push(item);
  }

  // Tri alphabétique dans chaque catégorie
  const sorted: Record<string, ComponentItem[]> = {};
  for (const cat of Object.keys(byCategory).sort()) {
    sorted[cat] = byCategory[cat].sort((a, b) => a.label.localeCompare(b.label));
  }
  return sorted;
}

const categoryIcons: Record<string, string> = {
  Azure:  azureIcon,
  AWS:    awsIcon,
  GCP:    gcpIcon,
  Fabric: "/icons/fabric/fabric_48_color.svg",
};

const DragSidebar = () => {
  const { t } = useTranslation('canvas');
  const [activeCategory, setActiveCategory] = useState<"Azure" | "AWS" | "GCP" | "Fabric">("Azure");
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  // ✅ Azure dynamique (backend)
  const { resources: azureResources, loading: azureLoading, error: azureError } = useAzureResources();

  // ✅ Construit Azure depuis backend (si OK) — et ne garde que le Top100
  const azureByCategory = useMemo(() => {
    if (azureLoading || azureError) return null;

    // Si le backend renvoie des ressources, on filtre selon top100, sinon on utilise directement top100
    const sourceKeys = (azureResources?.length ? azureResources : azureTop100Whitelist)
      .filter((k) => azureTop100Whitelist.includes(k as any));

    return buildTop100Resources(sourceKeys, azureTop100Meta, azureIconsByTypeTop100);
  }, [azureResources, azureLoading, azureError]);

  // ✅ AWS Top100
  const awsByCategory = useMemo(() => {
    return buildTop100Resources(awsTop100Whitelist, awsTop100Meta, awsIconsByTypeTop100);
  }, []);

  // ✅ GCP Top100
  const gcpByCategory = useMemo(() => {
    return buildTop100Resources(gcpTop100Whitelist, gcpTop100Meta, gcpIconsByTypeTop100);
  }, []);

  // ✅ Fabric
  const fabricByCategory = useMemo(() => {
    return buildTop100Resources(fabricTop100Whitelist as unknown as string[], fabricTop100Meta, fabricIconsByTypeTop100);
  }, []);

  // ✅ Sélection du fournisseur actif
  const componentsByCategory: Record<string, ComponentItem[]> = useMemo(() => {
    if (activeCategory === "Azure")  return azureByCategory ?? {};
    if (activeCategory === "AWS")    return awsByCategory;
    if (activeCategory === "Fabric") return fabricByCategory;
    return gcpByCategory;
  }, [activeCategory, azureByCategory, awsByCategory, gcpByCategory, fabricByCategory]);

  const onDragStart = (event: React.DragEvent, nodeType: string, label: string, icon: string) => {
    event.dataTransfer.setData("application/reactflow", nodeType);
    event.dataTransfer.setData("app/label", label);
    event.dataTransfer.setData("app/icon", icon);
    event.dataTransfer.effectAllowed = "move";
  };

  const toggleGroup = (group: string) => {
    setExpandedGroups((prev) => ({ ...prev, [group]: !prev[group] }));
  };

  return (
    <aside
      style={{
        width: 250,
        backgroundColor: "#f8f9fa",
        borderRight: "1px solid #ccc",
        position: "sticky",
        top: 0,
        height: "100vh",
        maxHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        overflow: "hidden",
        zIndex: 2,
      }}
    >
      {/* Section fixe en haut */}
      <div style={{ padding: 10, paddingBottom: 0, flexShrink: 0 }}>
        {/* Onglets fournisseurs */}
        <div style={{ display: "flex", justifyContent: "space-around", marginBottom: 10 }}>
          {(["Azure", "AWS", "GCP", "Fabric"] as const).map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              style={{
                background: "none",
                border: "none",
                padding: 6,
                cursor: "pointer",
                borderBottom: activeCategory === cat ? "2px solid #0078d4" : "2px solid transparent",
                fontWeight: activeCategory === cat ? "bold" : "normal",
                color: activeCategory === cat ? "#000" : "#777",
              }}
              title={cat}
            >
              <img
                src={categoryIcons[cat]}
                alt={cat}
                style={{ width: 28, height: 28, opacity: activeCategory === cat ? 1 : 0.5 }}
              />
            </button>
          ))}
        </div>

        {/* Barre de recherche */}
        <input
          type="text"
          placeholder={t('sidebar.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            marginBottom: 10,
            padding: "6px 8px",
            border: "1px solid #ccc",
            borderRadius: 4,
            fontSize: 12,
            width: "100%",
          }}
        />

        {/* Messages backend Azure */}
        {activeCategory === "Azure" && azureLoading && (
          <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
            {t('sidebar.loadingAzure')}
          </div>
        )}
        {activeCategory === "Azure" && azureError && (
          <div style={{ fontSize: 12, color: "red", marginBottom: 8 }}>
            {t('sidebar.errorAzure', { error: azureError })}
          </div>
        )}
      </div>

      {/* Section scrollable pour les composants */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          maxHeight: "calc(85vh - 140px)",
          overflowY: "auto",
          overflowX: "hidden",
          padding: "10px",
          paddingBottom: "20px",
        }}
      >
        {/* Groupes de composants */}
        {Object.entries(componentsByCategory).map(([category, components]) => {
        const filtered = components.filter((comp) => {
          const q = searchTerm.toLowerCase();
          return comp.label.toLowerCase().includes(q) || comp.type.toLowerCase().includes(q);
        });
        if (filtered.length === 0) return null;

        return (
          <div key={category} style={{ marginBottom: 12 }}>
            <div
              onClick={() => toggleGroup(category)}
              style={{
                fontWeight: "bold",
                color: "#000",
                cursor: "pointer",
                fontSize: 14,
                padding: "4px 0",
                borderBottom: "1px solid #ddd",
              }}
            >
              {category.toUpperCase()} ({filtered.length}) {expandedGroups[category] ? "▲" : "▼"}
            </div>
            {expandedGroups[category] && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 6 }}>
                {filtered.map(({ type, icon, label }) => (
                  <div
                    key={type}
                    draggable
                    onDragStart={(e) => onDragStart(e, type, label, icon)}
                    title={label}
                    style={{
                      width: 35,
                      height: 35,
                      border: "1px solid #ccc",
                      borderRadius: 6,
                      backgroundColor: "white",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      cursor: "grab",
                    }}
                  >
                    <img
                      src={icon}
                      alt={label}
                      style={{ width: 25, height: 25, objectFit: "contain" }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
      </div>
    </aside>
  );
};

export default DragSidebar;
